import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "../../app";
import { FETCH_INTERVAL } from "../constants";
import selectUserInputIds from "../../user/selectors/userInputIdsSelector";
import { useLazyGetDocumentSetCountQuery } from "../store/documentSetApi";
import { CounterState } from "../interfaces/documentSet";
import useIsAuthenticated from "../../user/hooks/useIsAuthenticated";

const useDocumentSetCounter = () => {
  const { isAuthenticated } = useIsAuthenticated();

  const [numberOfDocumentSets, setNumberOfDocumentSets] =
    useState<CounterState>({
      overview: -1,
      error: -1,
      remove: -1,
    });

  const inputIds = useAppSelector(selectUserInputIds);

  const { fetchDocumentSetsTrigger } = useAppSelector(
    (state) => state.documentSetsReducer,
  );

  const [getDocumentSetCount] = useLazyGetDocumentSetCountQuery();

  const fetchNumberOfDocumentSets = useCallback(
    async (signal: AbortSignal) => {
      if (!inputIds.length) {
        return;
      }

      const idString = inputIds.join(",");
      const request = getDocumentSetCount(idString);
      // The caller's signal fires on unmount and between poll intervals; without
      // forwarding it to the query, `controller.abort()` had no effect and the
      // in-flight request always ran to completion.
      const abortRequest = () => request.abort();
      signal.addEventListener("abort", abortRequest);

      try {
        const data = await request.unwrap();
        setNumberOfDocumentSets(data);
      } catch {
        return;
      } finally {
        signal.removeEventListener("abort", abortRequest);
      }
    },
    [inputIds, getDocumentSetCount],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const controller = new AbortController();

    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetchNumberOfDocumentSets only calls setNumberOfDocumentSets after awaiting the RTK Query request, so no state is set during the effect's synchronous pass. The rule cannot see through the async boundary.
    fetchNumberOfDocumentSets(controller.signal);

    const interval = setInterval(async () => {
      await fetchNumberOfDocumentSets(controller.signal);
    }, FETCH_INTERVAL);

    return () => {
      clearInterval(interval);
      controller.abort();
    };
  }, [fetchNumberOfDocumentSets, isAuthenticated, fetchDocumentSetsTrigger]);

  return numberOfDocumentSets;
};

export default useDocumentSetCounter;
